import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/** Body for "What is UEBA?" guide. */
export function WhatIsUebaGuideBody() {
  return (
    <>
      <Lead>
        UEBA, User and Entity Behavior Analytics, is the part of modern security
        monitoring that learns what normal looks like for each user and system, then
        flags the behaviour that deviates. It catches threats that static rules miss:
        a compromised account, an insider going rogue, or lateral movement that each
        looks individually harmless. This guide explains what UEBA is, how it works,
        and how it fits a SIEM.
      </Lead>

      <GuideSection id="what-is-ueba" title="What is UEBA?">
        <P>
          <Term>User and Entity Behavior Analytics (UEBA)</Term> is a security
          technology that uses statistical analysis and machine learning to build a{' '}
          <Term>behavioral baseline</Term> for users and entities, then detects
          anomalies that may indicate a threat. Rather than asking{' '}
          <Term>&quot;did this match a known-bad rule?&quot;</Term>, UEBA asks{' '}
          <Term>&quot;is this behaviour normal for this user or system?&quot;</Term>
        </P>
      </GuideSection>

      <GuideSection id="why-entity" title="Why the &quot;Entity&quot; matters">
        <P>
          The earlier term was UBA: just <Term>user</Term> behavior analytics. The{' '}
          <Term>E</Term> was added because attacks do not only involve user accounts.
          An <Term>entity</Term> can be a host, server, application, service account,
          or IP address. Modelling entities as well as users catches threats that
          have no human at the keyboard: a compromised service account, a server
          beaconing to a command-and-control host, or a database accessed in a way it
          never normally is.
        </P>
      </GuideSection>

      <GuideSection id="how-it-works" title="How UEBA works">
        <OL>
          <LI><Term>Baseline</Term>: learn each user&apos;s and entity&apos;s normal pattern (login times, locations, data volumes, peer-group behaviour).</LI>
          <LI><Term>Compare</Term>: measure current activity against that baseline, accounting for daily and weekly rhythms.</LI>
          <LI><Term>Score the anomaly</Term>: the more abnormal the behaviour, the higher the risk it contributes.</LI>
          <LI><Term>Feed risk</Term>: anomaly risk is attributed to the user or entity and, in a modern SIEM, added to its overall risk score.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="what-it-catches" title="What UEBA catches">
        <UL>
          <LI><Term>Compromised accounts</Term>: credentials used from a new location, device, or at an impossible travel speed.</LI>
          <LI><Term>Insider threats</Term>: a user suddenly accessing or exfiltrating data outside their normal scope.</LI>
          <LI><Term>Lateral movement</Term>: an entity reaching systems it has never touched before.</LI>
          <LI><Term>First-seen activity and volume spikes</Term>: new behaviours and unusual surges that no static threshold anticipated.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="ueba-and-siem" title="UEBA and SIEM">
        <P>
          UEBA is most useful as part of a SIEM rather than a standalone tool. The
          SIEM collects and correlates the evidence; UEBA turns behaviour into a{' '}
          <Term>risk signal</Term>; and{' '}
          <GuideLink href="/guides/what-is-risk-based-alerting">risk-based alerting</GuideLink>{' '}
          combines that with detections and threat intel into one prioritised view.
          Together they help analysts decide which entity actually needs attention.
          See <GuideLink href="/guides/what-is-siem">what is SIEM</GuideLink> for the
          bigger picture.
        </P>
        <Callout title="Anomalies vs. thresholds">
          <p>
            Static thresholds catch the problems you can name in advance. UEBA
            anomalies catch the ones you cannot: behaviour that is wrong{' '}
            <Term>for this entity</Term> even while still inside a fixed limit.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse does UEBA">
        <P>
          In LogPulse, behavioral analytics, impossible travel, first-seen activity,
          and volume spikes, feed the same risk model as detections and threat
          intelligence. Each anomaly contributes to an entity&apos;s bounded 0–100
          risk score, so behaviour and known-bad signals are weighed together rather
          than in separate tools. See{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> for how UEBA,
          detections and threat intel converge on one score.
        </P>
      </GuideSection>
    </>
  );
}
